import React, { useRef, useEffect } from "react";

interface DotGridProps {
  impulseRef: React.MutableRefObject<number | null>;
  /** When set, triggers the drift-right exit animation */
  exitRef?: React.MutableRefObject<number | null>;
  reverseWave?: boolean;
  cols?: number;
  rows?: number;
  gap?: number;
  fullOpacity?: boolean;
}

const IMPULSE_DURATION = 2200;
const EXIT_DURATION = 1800;

const DotGrid: React.FC<DotGridProps> = ({
  impulseRef,
  exitRef,
  reverseWave = false,
  cols = 26,
  rows = 22,
  gap = 30,
  fullOpacity = false,
}) => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const COLS = cols;
    const ROWS = rows;
    const GAP = gap;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = COLS * GAP;
    const H = ROWS * GAP;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.scale(dpr, dpr);

    const BLOB_PTS = 8;
    type DotSeed = {
      phaseOffset: number;
      speedMult: number;
      sizeMult: number;
      dx: number;
      dy: number;
      blobVars: number[];
    };
    const seeds: DotSeed[][] = Array.from({ length: ROWS }, () =>
      Array.from({ length: COLS }, () => ({
        phaseOffset: Math.random() * Math.PI * 2,
        speedMult: 0.6 + Math.random() * 0.8,
        sizeMult: 0.75 + Math.random() * 0.5,
        dx: (Math.random() - 0.5) * 6,
        dy: (Math.random() - 0.5) * 6,
        blobVars: Array.from(
          { length: BLOB_PTS },
          () => 0.7 + Math.random() * 0.6,
        ),
      })),
    );

    const drawBlob = (
      cx: number,
      cy: number,
      radius: number,
      vars: number[],
    ) => {
      const N = vars.length;
      const pts = vars.map((v, i) => {
        const a = (i / N) * Math.PI * 2 - Math.PI / 2;
        return {
          x: cx + Math.cos(a) * radius * v,
          y: cy + Math.sin(a) * radius * v,
        };
      });
      ctx.beginPath();
      for (let i = 0; i < N; i++) {
        const curr = pts[i];
        const next = pts[(i + 1) % N];
        const mx = (curr.x + next.x) / 2;
        const my = (curr.y + next.y) / 2;
        if (i === 0) ctx.moveTo(mx, my);
        ctx.quadraticCurveTo(curr.x, curr.y, mx, my);
      }
      ctx.closePath();
      ctx.fill();
    };

    let frame = 0;
    let raf: number;

    const tick = () => {
      ctx.clearRect(0, 0, W, H);

      // Impulse wave
      const impulseStart = impulseRef.current;
      let impulseProgress = -1;
      if (impulseStart !== null) {
        const elapsed = performance.now() - impulseStart;
        if (elapsed < IMPULSE_DURATION) {
          impulseProgress = elapsed / IMPULSE_DURATION;
        } else {
          impulseRef.current = null;
        }
      }

      // Exit drift
      const exitStart = exitRef?.current ?? null;
      let exitP = -1;
      if (exitStart !== null) {
        exitP = Math.min((performance.now() - exitStart) / EXIT_DURATION, 1);
      }

      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const s = seeds[r][c];
          let x = c * GAP + GAP / 2 + s.dx;
          let y = r * GAP + GAP / 2 + s.dy;
          const nr = r / (ROWS - 1);
          const nc = c / (COLS - 1);
          const d = nr * 0.6 + nc * 0.4;
          const baseR = (0.5 + d * 5.5) * s.sizeMult;
          const wave =
            (Math.sin(
              (r * 0.38 + c * 0.3) * 1.1 -
                frame * 0.035 * s.speedMult +
                s.phaseOffset,
            ) +
              1) *
            0.5;

          // Impulse boost
          let impulseBoost = 0;
          if (impulseProgress >= 0) {
            const waveFrontC = reverseWave
              ? COLS * impulseProgress
              : (COLS / 2 + 1) * (1 - impulseProgress);
            const dist = Math.abs(c - waveFrontC);
            const decay = Math.pow(1 - impulseProgress, 0.5);
            impulseBoost = decay * Math.exp((-dist * dist) / 1.5);
          }

          const combined = wave + impulseBoost * 1.4;
          const radius = Math.max(baseR * (0.4 + combined * 0.9), 0.3);
          const horizFade = fullOpacity ? 1 : Math.pow(nc, 0.6);
          let alpha = (0.04 + d * 0.5 + combined * 0.2) * 0.45 * horizFade;

          const impulseRatio = Math.min(impulseBoost * 2.0, 1);
          let rCol = Math.round(29 + (43 - 29) * impulseRatio);
          let gCol = Math.round(47 + (97 - 47) * impulseRatio);
          let bCol = Math.round(65 + (97 - 65) * impulseRatio);

          if (impulseRatio > 0.1)
            alpha = Math.min(alpha + impulseRatio * 0.5, 1);

          // Exit drift: blobs fly rightward with per-blob variation
          if (exitP >= 0) {
            const eased = exitP * exitP; // accelerates
            x += eased * W * 0.45 * s.speedMult; // rightward drift, speed varies
            y += eased * GAP * 4 * (s.dy >= 0 ? 1 : -1); // slight vertical scatter
            const colDelay = (c / COLS) * 0.3; // right cols linger a bit longer
            const fadeP = Math.max(0, exitP - colDelay * 0.5);
            alpha *= Math.max(0, 1 - fadeP * 1.6);
          }

          if (alpha <= 0.005) continue;

          ctx.fillStyle = `rgba(${rCol},${gCol},${bCol},${alpha.toFixed(2)})`;
          drawBlob(x, y, radius, s.blobVars);
        }
      }

      frame++;
      raf = requestAnimationFrame(tick);
    };

    tick();
    return () => cancelAnimationFrame(raf);
  }, [impulseRef, exitRef, reverseWave, cols, rows, gap, fullOpacity]);

  return <canvas ref={ref} style={{ display: "block" }} />;
};

export default DotGrid;
